import { APIProvider } from "../../AppController";
import { APIRoutes } from "../../config";
import {
  getNeoConversationShare,
  setNeoConversationShareClinics,
} from "../../pages/Neo/neoApi";

/**
 * ui2026 workstream: starting a Neo chat from a PSN flag and the side panel
 * that accompanies it. Kept separate from the legacy Neo code so this feature
 * is cleanly isolated.
 */

/** Snapshot the backend stores on `neo_conversations.extra_info.patient_panel`
 *  (built by `app/helper/Neo/flag_intro.py::build_patient_panel`). The panel
 *  formats the display values (age, consent label, payer, dates) itself. */
export interface FlagPatientPanel {
  patient_id: number;
  name: string;
  gender?: string | null;
  dob?: string | null;
  consented_status?: number | null;
  date_of_admission?: string | null;
  payer_information_json?: string | null;
  last_flag?: string | null;
  room?: string | null;
  alert_id: string;
  clinic_id: number;
  /** "device" (psnv2_flags) or "deviceless" (psn_deviceless_flag) — selects the
   *  ack endpoint used by the "Mark as reviewed" action. */
  flag_kind?: string | null;
  /** Whether the flag was already acked when the chat was started (snapshot). */
  reviewed?: boolean;
}

/**
 * Start a Neo conversation seeded from a PSN flag. The backend
 * (`POST /neo/conversations/from-flag`) creates a patient-scoped conversation
 * whose first message is an assistant summary of the flag. Returns the new
 * conversation's numeric id (open it with `/neo?conv=<id>`).
 */
export const createNeoConversationFromFlag = async (
  clinicId: number,
  alertId: string
): Promise<{ id: number }> => {
  return (await APIProvider.POST(APIRoutes.apiNeoConversationFromFlag, {
    clinic_id: clinicId,
    alert_id: alertId,
    first_client: "cip-web",
  })) as { id: number };
};

/**
 * Acknowledge ("mark as reviewed") a PSN flag. Routes to the correct backend
 * endpoint by flag kind: device flags (`psnv2_flags`) use `.../ack`, deviceless
 * flags (`psn_deviceless_flag`) use `.../ack-deviceless`. Both return
 * `{ message: "success" }` on success or `{ detail: { message } }` on error
 * (APIProvider resolves HTTP errors rather than throwing).
 */
export const reviewFlag = async ({
  clinicId,
  alertId,
  flagKind,
}: {
  clinicId: number;
  alertId: string;
  flagKind?: string | null;
}): Promise<{ ok: boolean; message?: string }> => {
  const base = `/patient-centric-app-v2/clinic/${clinicId}/psn_v2/alert/${encodeURIComponent(
    alertId
  )}/ack`;
  const path = flagKind === "deviceless" ? `${base}-deviceless` : base;
  const res = (await APIProvider.PUT(path, {})) as
    | { message?: string; detail?: { message?: string } }
    | undefined;
  const ok = res?.message === "success" && !res?.detail;
  return { ok, message: res?.detail?.message ?? res?.message };
};

/** Share a conversation with everyone in a clinic (visibility flips to
 *  shared_clinic server-side). */
export const shareConversationToClinic = async (
  conversationId: number,
  clinicId: number
): Promise<void> => {
  // APIProvider resolves HTTP/network failures into an error payload instead of
  // rejecting, so a failed share would otherwise look successful. Confirm the
  // server actually recorded this clinic and throw otherwise, so callers'
  // `.catch()` can warn the user that the copied link wasn't granted access.
  //
  // The POST replaces the whole selected-clinic set, so merge with the current
  // shares (minus locked ones, which the server keeps and this user can't set)
  // instead of dropping clinics the conversation was already shared with.
  const current = await getNeoConversationShare(conversationId);
  if (!Array.isArray(current?.clinic_ids)) {
    // Unknown current state — don't risk overwriting existing shares.
    throw new Error("Could not load the conversation's sharing settings.");
  }
  const locked = new Set(current.locked_clinic_ids ?? []);
  if (current.clinic_ids.includes(clinicId) || locked.has(clinicId)) return;
  const nextIds = Array.from(
    new Set([...current.clinic_ids.filter((id) => !locked.has(id)), clinicId])
  );
  const state = await setNeoConversationShareClinics(conversationId, nextIds);
  const shared =
    !!state &&
    (state.clinic_ids?.includes(clinicId) ||
      state.locked_clinic_ids?.includes(clinicId));
  if (!shared) {
    throw new Error(
      "The conversation was not shared with the requested clinic."
    );
  }
};

/** Deep link to a conversation using the app's hash router (`#/neo?conv=<id>`).
 *  Mirrors the workflow-runs deep-link helper. */
export const buildConversationDeepLink = (backendId: number): string => {
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}#/neo?conv=${backendId}`;
};
